import i18n from '@/common/i18n';
import createNetworkRoleset from '@/common/store/modules/rolesets/createRoleset';
import createNetworkRole from '@/common/store/modules/rolesets/createNetworkRole';
import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import { NotificationType } from '@/common/constants/notifications.constants';

const actions = {
  FETCH_NETWORK_ROLE_SETS({ commit }, payload = {}) {
    const { params, options } = payload;
    return this.$api.get('/api/v2/network/interface/roles_sets', params, options)
      .then((res) => {
        const rolesets = res.data.data.map(roleset => createNetworkRoleset(roleset));
        commit('SET_ROLESETS', { rolesets });
        return res;
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },

  FETCH_NETWORK_ROLES({ commit }, payload = {}) {
    const { params, options } = payload;
    return this.$api.get('/api/v2/network/interface/roles', params, options)
      .then((res) => {
        const networkRoles = res.data.data.map(role => createNetworkRole(role));

        commit('SET_NETWORK_ROLES', { networkRoles });
        return res;
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },

  FETCH_NETWORK_ROLE_SET({ commit }, id) {
    return this.$api.get(`/api/v2/network/interface/roles_sets/${id}`)
      .then((res) => {
        const roleset = createNetworkRoleset(res.data);

        commit('CHANGE_NETWORK', roleset);
        return res;
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },

  CREATE_NETWORK_ROLE_SET({ commit }, network) {
    return this.$api.post('/api/v2/network/interface/roles_sets', network)
      .then(() => {
        commit('CLEAR_NETWORKS_ERRORS');
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        // Needed for dispatch error returning in component
        throw error;
      });
  },

  CREATE_NETWORK_ROLE({ commit }, role) {
    return this.$api.post('/api/v2/network/interface/roles', role)
      .then((res) => {
        commit('ADD_NETWORK_ROLE', createNetworkRole(res.data));
        commit('CLEAR_NETWORKS_ERRORS');
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        // Needed for dispatch error returning in component
        throw error;
      });
  },

  DELETE_NETWORK_ROLE_SET({ commit }, networkId) {
    return this.$api.delete(`/api/v2/network/interface/roles_sets/${networkId}`)
      .then(() => {
        commit('REMOVE_NETWORK', networkId);
        commit('CLEAR_NETWORKS_ERRORS');
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },

  DELETE_NETWORK_ROLE({ commit }, roleName) {
    return this.$api.delete(`/api/v2/network/interface/roles/${roleName}`)
      .then(() => {
        commit('REMOVE_NETWORK_ROLE', roleName);
        commit('CLEAR_NETWORKS_ERRORS');
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },

  UPDATE_NETWORK_ROLE_SET({ commit }, network) {
    return this.$api.put(`/api/v2/network/interface/roles_sets/${network.id}`, network)
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        // Needed for dispatch error returning in component
        throw error;
      });
  },

  UPDATE_NETWORK_ROLE_SETS({ commit }, networks) {
    return this.$api.put('/api/v2/network/interface/roles_sets', {
      roles_sets: networks
    })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        // Needed for dispatch error returning in component
        throw error;
      });
  },

  UPDATE_NETWORK_ROLE({ commit }, networkRole) {
    return this.$api.put(`/api/v2/network/interface/roles/${networkRole.oldName || networkRole.name}`, networkRole)
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        // Needed for dispatch error returning in component
        throw error;
      });
  },
  NETWORK_ENABLE_ENCRYPTION({ commit }, network) {
    return this.$api.post('/api/v2/network/ipsec/enable', network)
      .then(() => {
        commit('SET_ENCRYPTION_TASK_STATE', TaskState.STARTING);
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },
  NETWORK_DISABLE_ENCRYPTION({ commit }, network) {
    return this.$api.post('/api/v2/network/ipsec/disable', network)
      .then(() => {
        commit('SET_ENCRYPTION_TASK_STATE', TaskState.STARTING);
      })
      .catch((error) => {
        commit('SET_NETWORKS_ERRORS', error);
        throw error;
      });
  },
  socket_networks({ commit, dispatch }, event) {
    if (event.type === 'roles_set') {
      switch (event.operation) {
        case 'deleted':
          return commit('REMOVE_NETWORK', event.object.id);
        case 'failed':
          return dispatch('notifications/ADD', {
            type: NotificationType.ERROR,
            title: event.object.name
              ? i18n.t('Operation with "{name}" failed', { name: event.object.name })
              : i18n.t('Operation failed'),
            message: event.object.exception
          }, { root: true });
        case 'updated':
        case 'created':
        default:
          return commit('CHANGE_NETWORK', createNetworkRoleset(event.object));
      }
    }
    return Promise.resolve();
  },
  socket_tasks({ dispatch }, task) {
    const tasksList = [
      TaskName.ROLE_SET_ENCRYPTION_ENABLE,
      TaskName.ROLE_SET_ENCRYPTION_DISABLE,
      TaskName.ROLE_SET_BULK_UPDATE
    ];

    if (tasksList.includes(task.name) && task.state === TaskState.SUCCESS) {
      dispatch('FETCH_NETWORK_ROLE_SETS');
    }
    if (TaskName.ROLE_SET_UPDATE === task.name && task.state === TaskState.SUCCESS) {
      dispatch('FETCH_NETWORK_ROLE_SET', task.result.id);
    }
  }
};

export default actions;
